import React from 'react';
import { cn } from '@/shared/utils';
import { TrendingUp, TrendingDown, CheckCircle, AlertCircle, Clock } from 'lucide-react';

// Warehouse Stats Card
export const WarehouseStatsCard = ({ 
  value, 
  label, 
  trend, 
  icon: Icon,
  status = 'default',
  className 
}) => {
  const statusStyles = {
    default: '',
    success: 'border-l-4 border-l-[var(--wh-success)]',
    warning: 'border-l-4 border-l-[var(--wh-warning)]',
    error: 'border-l-4 border-l-[var(--wh-error)]',
    info: 'border-l-4 border-l-[var(--wh-primary)]',
  };

  return (
    <div className={cn('wh-stats-card', statusStyles[status], className)}>
      {Icon && (
        <Icon 
          className="w-5 h-5 mb-2" 
          style={{ color: `var(--wh-${status === 'default' ? 'text-secondary' : status})` }}
        />
      )}
      <div className="wh-stats-value">{value}</div>
      <div className="wh-stats-label">{label}</div>
      {trend && (
        <div className={cn(
          'wh-stats-trend flex items-center justify-center gap-1',
          trend.direction === 'up' ? 'text-[var(--wh-success)]' : 'text-[var(--wh-error)]'
        )}>
          {trend.direction === 'up' ? (
            <TrendingUp className="w-3 h-3" />
          ) : (
            <TrendingDown className="w-3 h-3" />
          )}
          <span>{trend.value}</span>
        </div>
      )}
    </div>
  );
};

// Warehouse Button
export const WarehouseButton = ({ 
  children, 
  variant = 'primary', 
  size = 'default',
  fullWidth = false,
  icon: Icon,
  loading = false,
  disabled = false,
  className,
  onClick,
  ...props 
}) => {
  const variantClasses = {
    primary: 'wh-btn-primary',
    secondary: 'wh-btn-secondary',
    ghost: 'wh-btn-ghost',
    success: 'wh-btn-success',
    danger: 'wh-btn-danger',
  };

  const sizeClasses = {
    small: 'text-sm py-2 px-3',
    default: '',
    large: 'wh-btn-large',
  };

  return (
    <button
      className={cn(
        'wh-btn',
        variantClasses[variant],
        sizeClasses[size],
        fullWidth && 'wh-btn-full',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
      disabled={disabled || loading}
      onClick={onClick}
      {...props}
    >
      {loading ? (
        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
      ) : (
        <>
          {Icon && <Icon className="w-5 h-5" />}
          {children}
        </>
      )}
    </button>
  );
};

// Warehouse Card
export const WarehouseCard = ({ 
  children, 
  elevated = false, 
  interactive = false,
  status = null,
  className,
  onClick,
  ...props 
}) => {
  const statusBorder = status ? {
    success: 'border-l-4 border-l-[var(--wh-success)]',
    warning: 'border-l-4 border-l-[var(--wh-warning)]',
    error: 'border-l-4 border-l-[var(--wh-error)]',
    info: 'border-l-4 border-l-[var(--wh-primary)]',
  }[status] : '';

  return (
    <div
      className={cn(
        'wh-card',
        elevated && 'wh-card-elevated',
        interactive && 'wh-card-interactive',
        statusBorder,
        className
      )}
      onClick={onClick}
      {...props}
    >
      {children}
    </div>
  );
};

// Warehouse Badge
export const WarehouseBadge = ({ children, variant = 'default', className }) => {
  const variantClasses = {
    default: 'wh-badge-default',
    success: 'wh-badge-success',
    warning: 'wh-badge-warning',
    error: 'wh-badge-error',
    info: 'wh-badge-info',
  };

  return (
    <span className={cn('wh-badge', variantClasses[variant], className)}>
      {children}
    </span>
  );
};

// Warehouse Progress Bar
export const WarehouseProgress = ({ value, max = 100, label, showPercentage = true, className }) => {
  const percentage = Math.min(100, Math.max(0, (value / max) * 100));

  return (
    <div className={cn('wh-progress-container', className)}>
      {(label || showPercentage) && (
        <div className="flex justify-between mb-1 text-xs">
          {label && <span className="wh-progress-label">{label}</span>}
          {showPercentage && <span className="wh-progress-percent">{Math.round(percentage)}%</span>}
        </div>
      )}
      <div className="wh-progress-bar">
        <div 
          className="wh-progress-fill" 
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

// Warehouse Task Card
export const WarehouseTaskCard = ({
  title,
  subtitle,
  status,
  icon: Icon,
  stats,
  onClick,
  className
}) => {
  const statusConfig = {
    pending: { color: 'var(--wh-warning)', icon: Clock, label: 'PENDING' },
    in_progress: { color: 'var(--wh-primary)', icon: AlertCircle, label: 'IN PROGRESS' },
    completed: { color: 'var(--wh-success)', icon: CheckCircle, label: 'COMPLETED' },
  };

  const config = statusConfig[status] || statusConfig.pending;
  const StatusIcon = config.icon;

  return (
    <div 
      className={cn('wh-task-card', className)}
      onClick={onClick}
    >
      <div className="flex items-center gap-3">
        <div 
          className="w-12 h-12 rounded-xl flex items-center justify-center"
          style={{ background: 'var(--wh-primary)', color: 'white' }}
        >
          {Icon && <Icon className="w-6 h-6" />}
        </div>
        <div className="flex-1">
          <h3 className="wh-task-title">{title}</h3>
          <p className="wh-task-subtitle">{subtitle}</p>
        </div>
        <div className="text-right">
          <span 
            className="wh-badge text-xs"
            style={{ background: `${config.color}20`, color: config.color }}
          >
            {config.label}
          </span>
        </div>
      </div>
      {stats && (
        <div className="flex gap-4 mt-3 pt-3 border-t" style={{ borderColor: 'var(--wh-divider)' }}>
          {stats.map((stat, i) => (
            <div key={i} className="flex items-center gap-1 text-xs">
              {stat.icon && <stat.icon className="w-3 h-3" style={{ color: 'var(--wh-text-tertiary)' }} />}
              <span style={{ color: 'var(--wh-text-secondary)' }}>{stat.label}:</span>
              <span className="font-semibold" style={{ color: 'var(--wh-text-primary)' }}>{stat.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Warehouse Scan Result
export const WarehouseScanResult = ({ barcode, status, message, timestamp }) => {
  const isSuccess = status === 'OK' || status === 'success';
  
  return (
    <div className={cn(
      'wh-scan-result',
      isSuccess ? 'wh-scan-success' : 'wh-scan-error'
    )}>
      <div className="flex items-center gap-3">
        {isSuccess ? (
          <CheckCircle className="w-5 h-5 text-[var(--wh-success)]" />
        ) : (
          <AlertCircle className="w-5 h-5 text-[var(--wh-error)]" />
        )}
        <div className="flex-1">
          <p className="font-mono text-sm font-medium" style={{ color: 'var(--wh-text-primary)' }}>
            {barcode}
          </p>
          {message && (
            <p className="text-xs mt-1" style={{ color: 'var(--wh-text-secondary)' }}>
              {message}
            </p>
          )}
        </div>
        {timestamp && (
          <span className="text-xs" style={{ color: 'var(--wh-text-tertiary)' }}>
            {new Date(timestamp).toLocaleTimeString()}
          </span>
        )}
      </div>
    </div>
  );
};

// Warehouse Bottom Navigation
export const WarehouseBottomNav = ({ items, activeItem, onItemClick }) => {
  return (
    <nav className="wh-bottom-nav">
      {items.map((item) => {
        const Icon = item.icon;
        const isActive = activeItem === item.id;
        return (
          <button
            key={item.id}
            className={cn('wh-nav-item', isActive && 'wh-nav-item-active')}
            onClick={() => onItemClick(item.id)}
          >
            <Icon className="wh-nav-icon" />
            <span className="wh-nav-label">{item.label}</span>
            {item.badge > 0 && (
              <span className="wh-nav-badge">{item.badge}</span>
            )}
          </button>
        );
      })}
    </nav>
  );
};

export default {
  WarehouseStatsCard,
  WarehouseButton,
  WarehouseCard,
  WarehouseBadge,
  WarehouseProgress,
  WarehouseTaskCard,
  WarehouseScanResult,
  WarehouseBottomNav,
};
